import { Injectable } from '@angular/core';
import { HttpClient, HttpBackend } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, map, shareReplay, tap } from 'rxjs/operators';


export interface ManualEntry {
  url: string;
  version_date: string;
}

export interface ManualSlugData {
  /** Display name set in the sidly-ai panel; absent in pre-2026-08 manifests. */
  label?: string;
  /** Sort weight set in the sidly-ai panel; lower comes first. */
  order?: number;
  current: { [lang: string]: ManualEntry };
  archive: { [lang: string]: ManualEntry[] };
}

export interface Manifest {
  generated_at: string;
  manuals: { [slug: string]: ManualSlugData };
}

/** One product's manual, ready to render — resolved for a single language. */
export interface ManualProduct {
  slug: string;
  label: string;
  order: number;
  url: string;
  versionDate: string;
  versionLabel: string;
  archive: { date: string; url: string }[];
}


/**
 * Reads manifest.json from sidly.eu/manual/ and exposes current + archive
 * versions of PDF manuals to components. Fails gracefully — callers should
 * use a hardcoded fallback URL when getCurrent() emits null, so a temporary
 * CDN/FTP outage does not break the help panel.
 *
 * getProducts() is the way new band models reach the help panel: the manifest
 * carries each product's display name and ordering, so a model added in the
 * sidly-ai panel shows up without any change here or in the templates.
 */
@Injectable({ providedIn: 'root' })
export class ManualService {
  // Must be the www host: sidly.eu answers 308 -> www.sidly.eu and that redirect
  // carries no Access-Control-Allow-Origin, so the browser aborts the request
  // before following it and every fetch silently fell back to hardcoded links.
  private readonly MANIFEST_URL = 'https://www.sidly.eu/manual/manifest.json';
  private readonly CACHE_KEY = 'sidly_manual_manifest_v1';
  private readonly CACHE_TTL_MS = 5 * 60 * 1000;
  private readonly DEFAULT_ORDER = 100;

  private manifest$: Observable<Manifest | null> | null = null;
  private readonly http: HttpClient;

  // Built on HttpBackend, not the injected HttpClient, so the request skips the
  // interceptor chain. HttpTokenInterceptor stamps X-Access-Token and
  // Content-Type on everything it sees; on a cross-origin call those turn a
  // simple GET into a preflighted one, and sidly.eu does not list x-access-token
  // in Access-Control-Allow-Headers, so the browser blocked every manifest fetch.
  // Bypassing also stops the session token from being sent to a third-party host.
  constructor(handler: HttpBackend) {
    this.http = new HttpClient(handler);
  }

  getManifest(): Observable<Manifest | null> {
    const cached = this.loadFromCache();
    if (cached) return of(cached);

    if (!this.manifest$) {
      const bucket = Math.floor(Date.now() / this.CACHE_TTL_MS);
      const url = `${this.MANIFEST_URL}?v=${bucket}`;
      this.manifest$ = this.http.get<Manifest>(url).pipe(
        tap(m => this.saveToCache(m)),
        catchError(err => {
          console.warn('[ManualService] manifest fetch failed, returning null', err);
          return of(null);
        }),
        shareReplay(1)
      );
    }
    return this.manifest$;
  }

  getCurrent(slug: string, lang: string): Observable<ManualEntry | null> {
    return this.getManifest().pipe(
      map(m => (m && m.manuals && m.manuals[slug] && m.manuals[slug].current[lang]) || null)
    );
  }

  getArchive(slug: string, lang: string): Observable<ManualEntry[]> {
    return this.getManifest().pipe(
      map(m => (m && m.manuals && m.manuals[slug] && m.manuals[slug].archive[lang]) || [])
    );
  }

  /**
   * Every product that has a manual in `lang`, ordered as configured in the
   * sidly-ai panel. Slugs listed in `excludeSlugs` are skipped — use it for
   * products the template already renders by hand, so they do not show twice.
   */
  getProducts(lang: string, excludeSlugs: string[] = []): Observable<ManualProduct[]> {
    return this.getManifest().pipe(
      map(m => this.toProducts(m, lang, excludeSlugs))
    );
  }

  /** '2025-11-03' -> '03.11.2025' */
  formatVersionDate(iso: string): string {
    if (!iso) return '';
    const parts = iso.split('-');
    if (parts.length === 3) return `${parts[2]}.${parts[1]}.${parts[0]}`;
    return iso;
  }

  /** True when the version is younger than `days` — drives the "NEW" badge. */
  isRecent(iso: string, days: number = 90): boolean {
    if (!iso) return false;
    const t = Date.parse(iso);
    if (isNaN(t)) return false;
    return Date.now() - t < days * 24 * 60 * 60 * 1000;
  }

  private toProducts(m: Manifest | null, lang: string, exclude: string[]): ManualProduct[] {
    if (!m || !m.manuals) return [];
    const skip = exclude || [];
    const out: ManualProduct[] = [];

    Object.keys(m.manuals).forEach(slug => {
      if (skip.indexOf(slug) !== -1) return;
      const data = m.manuals[slug];
      const current = data && data.current && data.current[lang];
      if (!current || !current.url) return;

      const archive = (data.archive && data.archive[lang]) || [];
      out.push({
        slug,
        label: data.label || this.prettifySlug(slug),
        order: typeof data.order === 'number' ? data.order : this.DEFAULT_ORDER,
        url: current.url,
        versionDate: current.version_date,
        versionLabel: this.formatVersionDate(current.version_date),
        archive: archive.map(e => ({
          date: this.formatVersionDate(e.version_date),
          url: e.url,
        })),
      });
    });

    out.sort((a, b) => (a.order - b.order) || a.label.localeCompare(b.label));
    return out;
  }

  /** Fallback name for manifests written before labels existed. */
  private prettifySlug(slug: string): string {
    return slug
      .split('_')
      .filter(p => !!p)
      .map(p => p.charAt(0).toUpperCase() + p.slice(1))
      .join(' ') || slug;
  }

  private loadFromCache(): Manifest | null {
    try {
      const raw = sessionStorage.getItem(this.CACHE_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (!parsed || Date.now() - parsed.ts > this.CACHE_TTL_MS) return null;
      return parsed.manifest;
    } catch {
      return null;
    }
  }

  private saveToCache(manifest: Manifest): void {
    try {
      sessionStorage.setItem(this.CACHE_KEY, JSON.stringify({ ts: Date.now(), manifest }));
    } catch {
      // sessionStorage may be full or disabled; silently ignore
    }
  }
}
